import { ALFactoryGlobal, IAugLoopRuntimeManager } from '@augloop/office-online';
import { ISessionCloseMessage } from '@augloop/session-protocol';
import { AddOperation, Document } from '@augloop/types-core';
import { APP_VERSION, APP_INTERNAL_NAME, AUGLOOP_CONNECT_TIMEOUT_MILLISECONDS } from '../../AppConstants';
import { AugloopError, AugloopErrors } from './AugloopErrors';
import { LogEvents } from '../../utils/LogEvents';
import { AugloopKeys } from './telemetry/AugloopKeys';
import { v4 as uuidv4 } from 'uuid';
import { OTelTelemetryLogger } from '../TelemetryLogger/Details/OTelTelemetryLogger';
import { getLoggerInstanceForAugLoop } from '../TelemetryLogger/Details/Logger';
import { DurationMeasureType } from '../interfaces/ILogEvent';
import { LogSingleEvent } from '../TelemetryLogger/LogEvent';
import { LogEventNames, ILogData } from "../interfaces/ITelemetryLogger";
import { LogError } from '../../utils/ErrorTelemetry';
import { ErrorStringFromException } from '../../utils/utils';
import { EventEmitter } from 'events';
import { IAugloopEventEmitter, AugloopEvent } from '../interfaces/IAugloopEventEmitter';

let initializeRuntimeManagerTask: Promise<void> | null = null;

const augloopEventEmitter: IAugloopEventEmitter = new EventEmitter();

// Create a document tile in the session. This is the root of all subsequent tiles that will be created.
function SubmitDocumentTile(runtimeManager: IAugLoopRuntimeManager) {
  const documentTile = new Document({
    isReadonly: false
  });

  const documentOperation = new AddOperation({
    parentPath: ['session'],
    items: [
      {
        id: 'doc',
        body: documentTile
      }
    ]
  });
  runtimeManager.submitOperation({ operation: documentOperation });
}

// After calling this function, the caller should call EnsureAugloopRuntimeManager and await it before using Augloop
export function StartInitializingRuntimeManager(serviceUrl: string): void {
  const guid: string = uuidv4();
  LogEvents.Augloop.Start([{ key: AugloopKeys.SessionGuid, value: guid}, { key: AugloopKeys.ServiceUrl, value: serviceUrl}]);
  LogEvents.Augloop.BeginDurationMeasure(DurationMeasureType.Normal, AugloopKeys.InitializeAugloopRuntimeManagerDuration);
  initializeRuntimeManagerTask = new Promise((resolve, reject) => {
    const alRuntimeManager: IAugLoopRuntimeManager = ALFactoryGlobal.getAugLoopRuntimeManager();

    // Give AL 30 seconds to initialize a session. Fail otherwise.
    const timeoutHandle = setTimeout(() => {
      reject(new AugloopError(AugloopErrors.SessionInitTimeout, 'Timeout when initializing augloop session'));
    }, AUGLOOP_CONNECT_TIMEOUT_MILLISECONDS);

    alRuntimeManager.setSendOTelEventCallback({
      sendOTelEvent: (<OTelTelemetryLogger>getLoggerInstanceForAugLoop()).LogEventCallbackForAL
    });

    const augloopRuntimeConfig = {
      serviceUrl,
      appName: APP_INTERNAL_NAME,
      appVersion: APP_VERSION,
      releaseAudienceGroup: '',
      releaseChannel: '',
      releaseFork: '',
      sessionId: guid,
      initSession: true,
      sessionCreationOptions: {
        // Triggered when an AL session connection is established
        onSessionConnect: (_isSeedingRequired: boolean, sessionUrl: string, _origin: string, _authToken: string) => {
          LogEvents.Augloop.AddData([{ key: AugloopKeys.SessionUrl, value: sessionUrl }]);
          clearTimeout(timeoutHandle);
          LogEvents.Augloop.End();
          LogSingleEvent<AugloopKeys>(LogEventNames.Augloop, [{ key: AugloopKeys.OnSessionConnect, value: true }]);
          resolve();
          augloopEventEmitter.emit(AugloopEvent.SessionConnect);
        },

        // Trigered when we disconnected from an AL session (likely due to network issues)
        onSessionDisconnect: (error?: string) => {
          const data: ILogData<AugloopKeys>[] = [{ key: AugloopKeys.OnSessionDisconnect, value: true }];

          if (error)
            data.push({ key: AugloopKeys.OnSessionDisconnectError, value: error });

          LogSingleEvent<AugloopKeys>(LogEventNames.Augloop, data);
        },

        // Triggered when the AL service intentionally closed the sesion.
        onSessionClose: (sessionCloseMessage: ISessionCloseMessage) => {
          const data: ILogData<AugloopKeys>[] = [{ key: AugloopKeys.OnSessionClose, value: true }];

          if (sessionCloseMessage.reconnectAllowed !== undefined)
            data.push({ key: AugloopKeys.OnSessionCloseReconnectAllowed, value: sessionCloseMessage.reconnectAllowed });

          if (sessionCloseMessage.reason !== undefined)
            data.push({ key: AugloopKeys.OnSessionCloseReason, value: JSON.stringify(sessionCloseMessage.reason) });

          LogSingleEvent<AugloopKeys>(LogEventNames.Augloop, data);
          augloopEventEmitter.emit(AugloopEvent.SessionClose);

          try {
            alRuntimeManager.startNewSession();
          } catch (e) {
            LogError(LogEventNames.Augloop, "Failed reconnecting after session close event: " + ErrorStringFromException(e));
          }
        }
      }
    };

    alRuntimeManager.initialize(augloopRuntimeConfig);
    SubmitDocumentTile(alRuntimeManager);
  });
}

export async function EnsureAugloopRuntimeManager() {
  await initializeRuntimeManagerTask;

  // A runtime manager represents a session in augloop, so we need to make sure we have one.
  // It exposes mechanisms to send and receive data from augloop in a session, so the
  // voice-related classes we depend on (VoiceSession, etc) need an instance of the manager
  // to communicate with AL.
  const alRuntimeManager = ALFactoryGlobal.getAugLoopRuntimeManager();

  if (alRuntimeManager == null)
    throw new Error('Augloop runtime manager is not initialized');

  return alRuntimeManager;
}

export function GetAugloopEventEmitter(): IAugloopEventEmitter {
  return augloopEventEmitter;
}